import React from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import {
  PageHeader,
  Head,
  PerformerAppPath,
  PerformerAppSmartBanner,
} from "component-library";
import { i18n, withTranslation, WithTranslation } from "i18n";
import {
  withLaunchDarklyConsumerHOC,
  LaunchDarklyClientWithFlags,
} from "shared-services";
import { MapDispatchToProps } from "shared/types/utilities";

import { PageTitleKey, SELF_TAPE_DETAIL_FULL_PATH } from "config/common";
import PageKey from "config/PageKey";

import { IApplicationState } from "store";
import { hidePopup, setLoading, showPopup } from "store/ui/ui.actions";
import { HidePopup, SetLoading, ShowPopup } from "store/ui/ui.types";

import { IWithRequestAbortController } from "types/axios";

import {
  ISelfTapeRequestNotification,
  ISelfTapeRequestNotificationFilterParams,
  ISelfTapeRequestNotificationFilters,
  ISelfTapeRequestNotificationListPaginated,
  ISelfTapeRequestNotificationStatus,
} from "models/selfTapeRequest";

import { getPageTitle } from "helpers/getPageTitle";
import selfTapeRequestNotificationService from "services/api/selfTapeRequest/selfTapeRequestNotificationService";

import withSelfTapeRequestNotificationsFilter, {
  IWithSelfTapeRequestNotificationsFilterOptions,
  IWithSelfTapeRequestNotificationsFilterProps,
  IWithSelfTapeRequestNotificationsFilterTexts,
} from "hoc/withSelfTapeRequestNotificationsFilter/withSelfTapeRequestNotificationsFilter";
import { FilterType } from "hoc/withSelfTapeRequestNotificationsFilter/withSelfTapeRequestNotificationsFilter.config";

import { JobsPageTab } from "pages/JobsPage/JobsPage.config";

import SelfTapeRequestNotificationTable, {
  ISelfTapeRequestNotificationTableTexts,
} from "components/SelfTapeRequestNotificationTable/SelfTapeRequestNotificationTable";
import { prepareTableTexts } from "components/SelfTapeRequestNotificationTable/SelfTapeRequestNotificationTable.helpers";
import {
  ISelfTapeRequestNotificationTableItem,
  SelfTapeRequestNotificationUuid,
} from "components/SelfTapeRequestNotificationTable/SelfTapeRequestNotificationTable.models";

import {
  prepareFilterControlTexts,
  prepareSelfTapeRequestItem,
} from "./SelfTapeRequestNotificationsPage.helpers";

import "./SelfTapeRequestNotificationsPage.scss";
import withRouter, { RouterProps } from "hoc/withRouter";
import { generatePath } from "react-router-dom";

export interface ISelfTapeRequestNotificationsPageTexts {
  table: ISelfTapeRequestNotificationTableTexts;
  pageTitle: string;
}

export interface ISelfTapeRequestNotificationsPageActions {
  setLoading: SetLoading;
  showPopup: ShowPopup;
  hidePopup: HidePopup;
}

export type ISelfTapeRequestNotificationsPageStoreProps = {
  texts: ISelfTapeRequestNotificationsPageTexts;
  filterControlTexts: IWithSelfTapeRequestNotificationsFilterTexts;
  convertToTableItems: (
    item: ISelfTapeRequestNotification[]
  ) => ISelfTapeRequestNotificationTableItem[];
  getSelfTapeRequestNotificationFilters(
    abortController?: AbortController
  ): Promise<ISelfTapeRequestNotificationFilters>;
  findSelfTapeRequestNotifications(
    params: ISelfTapeRequestNotificationFilterParams,
    abortController?: AbortController
  ): Promise<ISelfTapeRequestNotificationListPaginated>;
  updateStatus(
    statuses: ISelfTapeRequestNotificationStatus[],
    abortController?: AbortController
  ): Promise<void>;
};

export interface ISelfTapeRequestNotificationsPageProps
  extends ISelfTapeRequestNotificationsPageStoreProps,
    RouterProps,
    ISelfTapeRequestNotificationsPageActions,
    IWithSelfTapeRequestNotificationsFilterProps,
    LaunchDarklyClientWithFlags,
    WithTranslation {}

export interface ISelfTapeRequestNotificationsPageState {
  selectedItems: SelfTapeRequestNotificationUuid[];
}

export class SelfTapeRequestNotificationsPage
  extends React.Component<
    ISelfTapeRequestNotificationsPageProps,
    ISelfTapeRequestNotificationsPageState
  >
  implements IWithRequestAbortController
{
  public static pageSize: number = 50;
  public abortController = new AbortController();

  public readonly state: Readonly<ISelfTapeRequestNotificationsPageState> = {
    selectedItems: [],
  };

  public componentWillUnmount() {
    this.abortController.abort?.();
  }

  public render() {
    const { t } = i18n;
    const {
      texts,
      renderSelfTapeRequestNotificationsFilterControl,
      filterValues,
      paginatedItems,
      isLoading,
    } = this.props;
    const pageTitle = t("common:mediaRequest.page.title");

    return (
      <section className="c-self-tape-request-notifications-page">
        <Head registerPageView={this.registerPageView}>
          <title>{pageTitle}</title>
        </Head>
        <PerformerAppSmartBanner appPath={PerformerAppPath.TapeRequests} />
        <PageHeader pageTitle={pageTitle} />
        {renderSelfTapeRequestNotificationsFilterControl()}
        <div className="g-content-area">
          <div className="g-col-lg-12 g-table-wrapper">
            <SelfTapeRequestNotificationTable
              texts={texts.table}
              items={paginatedItems.items}
              selectedItems={this.state.selectedItems}
              deselectItems={this.deselectItems}
              selectItems={this.selectItems}
              onItemClick={this.handleItemClick}
              itemsPerPage={SelfTapeRequestNotificationsPage.pageSize}
              isPageLoading={isLoading}
              pageNumber={filterValues[FilterType.PageNumber]}
              itemsCount={paginatedItems.count}
              updatePageNumber={this.handleUpdatePageNumber}
              onMarkAsRead={this.handleMarkAsRead}
              onMarkAsUnread={this.handleMarkAsUnread}
              returnUri="#"
            />
          </div>
        </div>
      </section>
    );
  }

  private deselectItems = (itemIds: SelfTapeRequestNotificationUuid[]) =>
    this.setState({
      selectedItems: this.state.selectedItems.filter(
        (item) => !itemIds.includes(item)
      ),
    });

  private selectItems = (itemIds: SelfTapeRequestNotificationUuid[]) =>
    this.setState({ selectedItems: [...this.state.selectedItems, ...itemIds] });

  private handleItemClick = async (
    item: ISelfTapeRequestNotificationTableItem
  ) => {
    const { router } = this.props;
    await this.handleMarkAsRead([item.id]);

    item.sendStats();

    router.navigate(
      generatePath(SELF_TAPE_DETAIL_FULL_PATH, { performerRequestId: item.id })
    );
  };

  private handleUpdatePageNumber = (page: number) => {
    const { router } = this.props;
    const [, setSearchParams] = router.searchParams;

    setSearchParams({ page: String(page) });
  };

  private deselectAllItems = () => {
    this.setState({ selectedItems: [] });
  };

  private handleMarkAsRead = async (
    itemIds: SelfTapeRequestNotificationUuid[]
  ) => {
    await this.props.onMarkAsRead(itemIds);
    this.deselectAllItems();
  };

  private handleMarkAsUnread = async (
    itemIds: SelfTapeRequestNotificationUuid[]
  ) => {
    await this.props.onMarkAsUnread(itemIds);
    this.deselectAllItems();
  };

  private registerPageView = () => {
    // register GTMEvent in a future ticket
  };
}

const mapStateToProps = (
  state: IApplicationState,
  props: ISelfTapeRequestNotificationsPageProps
): ISelfTapeRequestNotificationsPageStoreProps => {
  const commonTranslations = state.translation.common;
  const pageTranslation =
    state.translation.pages[PageKey.SelfTapeRequestNotifications]!;
  const userType = state.user.data!.type;

  return {
    texts: {
      table: prepareTableTexts(commonTranslations),
      pageTitle: getPageTitle(
        commonTranslations,
        PageTitleKey.JobsFeedSelfTapeRequests
      ),
    },
    convertToTableItems: (items) =>
      items.map((item) =>
        prepareSelfTapeRequestItem({ item, userType, t: props.t })
      ),
    filterControlTexts: prepareFilterControlTexts(pageTranslation),
    findSelfTapeRequestNotifications: (...args) =>
      selfTapeRequestNotificationService.find(...args),
    getSelfTapeRequestNotificationFilters: (...args) =>
      selfTapeRequestNotificationService.getFilters(...args),
    updateStatus: (...args) =>
      selfTapeRequestNotificationService.updateStatus(...args),
  };
};

const mapDispatchToProps: MapDispatchToProps<ISelfTapeRequestNotificationsPageActions> =
  {
    setLoading,
    showPopup,
    hidePopup,
  };

export const withSelfTapeRequestNotificationsFilterOptions: IWithSelfTapeRequestNotificationsFilterOptions =
  {
    tab: JobsPageTab.SelfTapeRequests,
  };

export default compose(
  withRouter,
  withTranslation(),
  withLaunchDarklyConsumerHOC(),
  connect(mapStateToProps, mapDispatchToProps),
  withSelfTapeRequestNotificationsFilter<ISelfTapeRequestNotificationsPageProps>(
    withSelfTapeRequestNotificationsFilterOptions
  )
)(SelfTapeRequestNotificationsPage);
